import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import {
  Add as AddIcon,
  InsertLink as LinkIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  VerifiedUser as VerifiedUserIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import './SharableLinksStats.css';

type Props = {
  /** Null while the first load is in flight: tiles are skipped, the actions still show. */
  totals: { links: number; opens: number; loggedInViews: number; leads: number } | null;
  loading: boolean;
  onRefresh: () => void;
  onCreate: () => void;
};

/** Counts up to `value` on first show and whenever it changes. */
function useCountUp(value: number, ms = 900) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(value); from.current = value; return; }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const next = origin + (value - origin) * (1 - Math.pow(1 - t, 3));
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
      else from.current = value;
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, ms]);
  return shown;
}

function StatTile({ tone, icon, label, value, suffix = '', decimals = 0, hint, meter }: {
  tone: 'orange' | 'pink' | 'teal' | 'violet' | 'gold';
  icon: React.ReactNode;
  label: string;
  value: number;
  suffix?: string;
  decimals?: number;
  hint: string;
  meter?: number;
}) {
  const shown = useCountUp(value);
  return (
    <Tooltip title={hint} placement="top">
      <div className={`sls-tile sls-tile--${tone}`}>
        <span className="sls-icon" aria-hidden="true">{icon}</span>
        <span className="sls-body">
          <span className="sls-value">{shown.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>
          <span className="sls-label">{label}</span>
        </span>
        {meter !== undefined && (
          <span className="sls-meter" aria-hidden="true"><i style={{ width: `${Math.min(100, Math.max(0, meter))}%` }} /></span>
        )}
      </div>
    </Tooltip>
  );
}

/** My Shareable Links totals, with the list's actions at the right end. */
export function SharableLinksStats({ totals, loading, onRefresh, onCreate }: Props) {
  const conversion = totals && totals.opens > 0 ? (totals.leads / totals.opens) * 100 : 0;
  return (
    <div className="sls">
      {totals && (
        <div className="sls-tiles">
          <StatTile tone="orange" icon={<LinkIcon />} label="Live links" value={totals.links} hint="Links buyers can still open" />
          <StatTile tone="pink" icon={<VisibilityIcon />} label="Total views" value={totals.opens} hint="Every time your links were opened, before or after the WhatsApp check" />
          <StatTile tone="teal" icon={<VerifiedUserIcon />} label="Logged-in views" value={totals.loggedInViews} hint="3D map views by buyers who verified on WhatsApp" />
          <StatTile tone="violet" icon={<PeopleIcon />} label="Leads" value={totals.leads} hint="WhatsApp-verified buyers, saved to Super Connector" />
          <StatTile tone="gold" icon={<TrendingUpIcon />} label="Views → leads" value={conversion} suffix="%" decimals={1} meter={conversion}
            hint="Share of total views that became a verified lead" />
        </div>
      )}
      <div className="sls-actions">
        <Tooltip title="Refresh">
          <span>
            <button type="button" className="sls-refresh" onClick={onRefresh} disabled={loading} aria-label="Refresh links">
              {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <RefreshIcon fontSize="small" />}
            </button>
          </span>
        </Tooltip>
        <button type="button" className="sls-create" onClick={onCreate}>
          <AddIcon fontSize="small" /> New link
        </button>
      </div>
    </div>
  );
}
